declare global {
    interface Window {
        snowplow?: (...args: unknown[]) => void;
        GlobalSnowplowNamespace?: string[];
        aiAssistantContexts?: Array<{ schema: string; data: Record<string, unknown> }>;
    }
}

// Configuration
// Using (Advisor template tracker)
const TRACKER_LIBRARY =
    process.env.REACT_APP_SNOWPLOW_TRACKER_URL ??
    'https://storage.googleapis.com/snowplow-cto-office-tracker-bucket/3.12.0/sp.js';

// Collector URL: test (step 3) vs prod (step 4) per onboarding. Chosen by build mode (NODE_ENV); override via REACT_APP_SNOWPLOW_COLLECTOR_URL when needed.
const COLLECTOR_URL = ((): string => {
    const envUrl = process.env.REACT_APP_SNOWPLOW_COLLECTOR_URL;
    if (envUrl) return envUrl.replace(/\/+$/, '');
    const isProduction = process.env.NODE_ENV === 'production';
    return isProduction ? 'https://snowplow.visma.com' : 'https://snowplow-test.visma.com';
})();

// App ID
const APP_ID = process.env.REACT_APP_SNOWPLOW_APP_ID ?? 'AIX';

// Namespace: Tracker instance identifier - default one
const TRACKER_NAMESPACE = process.env.REACT_APP_SNOWPLOW_NAMESPACE ?? 'cf';

let isInitialized = false;

// --------------- Context interfaces (User and Customer Segmentation) ---------------
// Guidelines: send customer_id (e.g. Visma CustomerID) and user via setUserId (e.g. User/EmployeeID).
// Do NOT populate customer_name, company_name, organization_no (PII).

export interface ContextCustomer {
    /** Customer identifier for Data Lake join (e.g. Visma CustomerID). Prefer this over PII. */
    customer_id?: string;
    business_unit?: string;
    countryiso?: string;
    customfield_customer1?: string;
    customfield_customer2?: string;
    customfield_customer3?: string;
    /** @deprecated Do not populate - PII. Kept for Iglu schema compatibility. */
    customer_name?: string;
    /** @deprecated Do not populate - PII. Kept for Iglu schema compatibility. */
    company_name?: string;
    /** @deprecated Do not populate - PII. Kept for Iglu schema compatibility. */
    organization_no?: string;
}

export interface ContextUser {
    user_type?: string;
    registration_date?: string;
    customfield_user1?: string;
    customfield_user2?: string;
    customfield_user3?: string;
}

// --------------- Product Breakdown ---------------

export interface ContextProduct {
    r12?: string;
    product_name?: string;
    module_name?: string;
    module_no?: string;
    customfield_product1?: string;
    customfield_product2?: string;
    customfield_product3?: string;
}

export interface ContextFeature {
    feature_name?: string;
    is_core_feature?: boolean;
}

export interface ContextProcess {
    process_name?: string;
    process_id?: string;
    process_step?: string;
}

export interface ContextCoreEvent {
    event_label?: string;
    is_core_event?: boolean;
}

// --------------- Other ---------------

export interface ContextCustom {
    custom_name?: string;
    customfield_custom1?: string;
    customfield_custom2?: string;
    customfield_custom3?: string;
}

export interface ContextDeletion {
    deletion_id?: string;
    deletion_id2?: string;
}

export interface ContextIdentifier {
    identifier_id?: string;
    identifier_type?: string;
}

// Init options - global contexts set at init
export interface SnowplowInitContexts {
    product?: ContextProduct;
    user?: ContextUser;
    customer?: ContextCustomer;
    deletion?: ContextDeletion;
}

// Schema URIs
const SCHEMAS = {
    customer: 'iglu:com.visma-tech-hubs/context_customer/jsonschema/1-0-0',
    user: 'iglu:com.visma-tech-hubs/context_user/jsonschema/1-0-0',
    product: 'iglu:com.visma-tech-hubs/context_product/jsonschema/1-0-0',
    feature: 'iglu:com.visma-tech-hubs/context_feature/jsonschema/1-0-0',
    process: 'iglu:com.visma-tech-hubs/context_process/jsonschema/1-0-0',
    coreEvent: 'iglu:com.visma-tech-hubs/context_core_event/jsonschema/1-0-0',
    custom: 'iglu:com.visma-tech-hubs/context_custom/jsonschema/1-0-0',
    deletion: 'iglu:com.visma-tech-hubs/context_deletion/jsonschema/1-0-0',
    identifier: 'iglu:com.visma-tech-hubs/context_identifier/jsonschema/1-0-1',
} as const;

// Build event-specific contexts (feature, process, core_event) - base contexts are added via addGlobalContexts
function buildEventContexts(overrides?: {
    feature?: ContextFeature;
    process?: ContextProcess;
    coreEvent?: ContextCoreEvent;
}): Array<{ schema: string; data: Record<string, unknown> }> | undefined {
    if (!overrides) return undefined;
    const contexts: Array<{ schema: string; data: Record<string, unknown> }> = [];
    if (overrides.feature && Object.keys(overrides.feature).length > 0) {
        contexts.push({ schema: SCHEMAS.feature, data: overrides.feature as Record<string, unknown> });
    }
    if (overrides.process && Object.keys(overrides.process).length > 0) {
        contexts.push({ schema: SCHEMAS.process, data: overrides.process as Record<string, unknown> });
    }
    if (overrides.coreEvent && Object.keys(overrides.coreEvent).length > 0) {
        contexts.push({ schema: SCHEMAS.coreEvent, data: overrides.coreEvent as Record<string, unknown> });
    }
    return contexts.length > 0 ? contexts : undefined;
}

// --------------- Init ---------------

/**
 * Initializes Snowplow when enabled. In production, call only when the EnableSnowplow feature flag is true.
 * @param enabled - When false, does not initialize (e.g. when FF is off in prod). Default true.
 */
export function initSnowplow(enabled?: boolean, userId?: string, initContexts?: SnowplowInitContexts): void {
    if (enabled === false) return;
    if (isInitialized || !COLLECTOR_URL) {
        if (!COLLECTOR_URL) {
            console.warn('Snowplow: Missing collector URL');
        }
        return;
    }

    // Load Snowplow script (Visma template IIFE loader - kept as-is for compatibility)
    if (!window.snowplow) {
        /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, prefer-rest-params, @typescript-eslint/no-unnecessary-type-assertion */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (function (p: any, l: Document, o: string, w: string, i: string) {
            if (!p[i]) {
                p.GlobalSnowplowNamespace = p.GlobalSnowplowNamespace ?? [];
                p.GlobalSnowplowNamespace.push(i);
                p[i] = function (...args: unknown[]) {
                    (p[i].q = p[i].q ?? []).push(args);
                };
                p[i].q = [];
                const n = l.createElement(o);
                const g = l.getElementsByTagName(o)[0];
                (n as HTMLScriptElement).async = true;
                (n as HTMLScriptElement).src = w;
                g.parentNode?.insertBefore(n, g);
            }
        })(window, document, 'script', TRACKER_LIBRARY, 'snowplow');
        /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, prefer-rest-params, @typescript-eslint/no-unnecessary-type-assertion */
    }

    // DEFINING CONTEXTS - User and Customer Segmentation, Product, Feature, Process, Core Event, Other
    const context_product: ContextProduct = initContexts?.product ?? {
        product_name: 'AI-Assistant',
        module_name: 'AI Assistant',
    };
    const context_user = initContexts?.user;
    const context_customer = initContexts?.customer;
    const context_deletion = initContexts?.deletion;

    // Place all contexts in a list. Remove contexts from lists if not used.
    const contexts: Array<{ schema: string; data: Record<string, unknown> }> = [
        { schema: SCHEMAS.product, data: context_product as Record<string, unknown> },
    ];
    if (context_user) {
        contexts.push({ schema: SCHEMAS.user, data: context_user as Record<string, unknown> });
    }
    if (context_customer) {
        contexts.push({ schema: SCHEMAS.customer, data: context_customer as Record<string, unknown> });
    }
    if (context_deletion) {
        contexts.push({ schema: SCHEMAS.deletion, data: context_deletion as Record<string, unknown> });
    }
    window.aiAssistantContexts = contexts;

    const sp = window.snowplow;
    if (!sp) return;

    // Initialize tracker
    sp('newTracker', TRACKER_NAMESPACE, COLLECTOR_URL, {
        appId: APP_ID,
        platform: 'web',
        contexts: {
            webPage: true,
            performanceTiming: true,
        },
    });

    if (userId) {
        sp('setUserId', userId);
    }

    // Add global contexts to all subsequent events
    sp('addGlobalContexts', contexts);

    // EVENT METHODS - Link click tracking (contexts come from addGlobalContexts)
    sp('enableLinkClickTracking', {
        pseudoClicks: true,
        trackContent: true,
    });

    // Enable page ping events - must be called before trackPageView
    sp('enableActivityTracking', {
        minimumVisitLength: 30,
        heartbeatDelay: 20,
    });

    // Initialize page view (contexts come from addGlobalContexts)
    sp('trackPageView', {
        title: document.title || 'AI Assistant',
    });

    isInitialized = true;

    if (process.env.NODE_ENV === 'development') {
        console.log('Snowplow initialized', {
            collector: COLLECTOR_URL,
            appId: APP_ID,
            namespace: TRACKER_NAMESPACE,
            contexts: contexts.length,
        });
        console.log('To test: run window.testSnowplow() in console');
    }
}

/**
 * Updates Snowplow identity after init. Call when user and/or host context become available
 * (e.g. after login or when host context is set). Adds customer, user, and deletion contexts
 * so events can be joined in Data Lake. Use only non-PII: customer_id, user via setUserId, user_type, deletion_id.
 */
export function updateSnowplowIdentity(userId?: string, initContexts?: SnowplowInitContexts): void {
    if (!isInitialized || !window.snowplow) return;

    const sp = window.snowplow;
    if (userId) {
        sp('setUserId', userId);
    }

    const identityContexts: Array<{ schema: string; data: Record<string, unknown> }> = [];
    if (initContexts?.customer && Object.keys(initContexts.customer).length > 0) {
        identityContexts.push({ schema: SCHEMAS.customer, data: initContexts.customer as Record<string, unknown> });
    }
    if (initContexts?.user && Object.keys(initContexts.user).length > 0) {
        identityContexts.push({ schema: SCHEMAS.user, data: initContexts.user as Record<string, unknown> });
    }
    if (initContexts?.deletion && Object.keys(initContexts.deletion).length > 0) {
        identityContexts.push({ schema: SCHEMAS.deletion, data: initContexts.deletion as Record<string, unknown> });
    }
    if (identityContexts.length > 0) {
        sp('addGlobalContexts', identityContexts);
    }
}

// Structured event with optional feature, process, core_event contexts (per-event)
export function trackStructEvent(
    category: string,
    action: string,
    options?: {
        label?: string;
        property?: string;
        value?: number;
        feature?: ContextFeature;
        process?: ContextProcess;
        coreEvent?: ContextCoreEvent;
    },
): void {
    if (!isInitialized || !window.snowplow) return;

    const eventContexts = buildEventContexts({
        feature: options?.feature,
        process: options?.process,
        coreEvent: options?.coreEvent,
    });

    window.snowplow('trackStructEvent', {
        category,
        action,
        label: options?.label,
        property: options?.property,
        value: options?.value,
        ...(eventContexts && eventContexts.length > 0 && { context: eventContexts }),
    });
}

// Test function
export function testSnowplowConnection(): void {
    if (!isInitialized || !window.snowplow) {
        console.error('Snowplow: Not initialized.');
        return;
    }
    console.log('Sending test events to:', COLLECTOR_URL);
    window.snowplow('trackPageView', { title: 'Snowplow Test' });
    window.snowplow('trackStructEvent', {
        category: 'test',
        action: 'connection_test',
        label: 'manual_test',
    });
    console.log('Check Network tab for POST to', `${COLLECTOR_URL}/com.snowplowanalytics.snowplow/tp2`);
}

if (typeof window !== 'undefined') {
    (window as Window & { testSnowplow?: () => void }).testSnowplow = testSnowplowConnection;
}

export { isInitialized };
